import { AddressesByType, LinxFormAddressModel } from '@shared/models';

import { addressSummaryLines } from './address-summary';

export interface ReusableAddress {
  key: string;
  line1: string;
  line2: string;
  address: LinxFormAddressModel;
}

// The parts an address is made of, and all a reused address copies: never its id, flags or contact name
const ADDRESS_PARTS = ['addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country'] as const;

const ADDRESS_LISTS: (keyof AddressesByType)[] = ['physicalAddresses', 'mailingAddresses', 'shippingAddresses'];

export const addressParts = (address?: Partial<LinxFormAddressModel>): Partial<LinxFormAddressModel> =>
  Object.fromEntries(ADDRESS_PARTS.map((part) => [part, address?.[part]]));

export const isBlankAddress = (address?: Partial<LinxFormAddressModel>): boolean => ADDRESS_PARTS.every((part) => !address?.[part]);

const addressKey = (address: Partial<LinxFormAddressModel>): string =>
  ADDRESS_PARTS.map((part) => (address[part] ?? '').trim().toUpperCase()).join('|');

const isComplete = (address?: Partial<LinxFormAddressModel>): address is LinxFormAddressModel =>
  !!(address?.addressLine1 && address.city && address.state && address.postalCode && address.country);

/**
 * The addresses a new address of one type can copy (KOR-15208, Figma 8280:8012): every complete address entered for
 * the other types, once each, leaving out any this type already has.
 */
export const reusableAddresses = (addresses: AddressesByType, own: keyof AddressesByType): ReusableAddress[] => {
  const taken = new Set((addresses[own] ?? []).filter(isComplete).map(addressKey));
  return ADDRESS_LISTS.filter((list) => list !== own)
    .flatMap((list) => addresses[list] ?? [])
    .filter(isComplete)
    .reduce<ReusableAddress[]>((found, address) => {
      const key = addressKey(address);
      if (taken.has(key) || found.some((candidate) => candidate.key === key)) return found;
      const [line1 = '', line2 = ''] = addressSummaryLines(address);
      return [...found, { key, line1, line2, address }];
    }, []);
};
